/**
 * Custom hook for subscribing to session and comment updates
 *
 * Uses Firestore real-time listeners with REST API polling fallback.
 *
 * Two-phase approach:
 *   Effect A (sessionId changes): Full reset + one-time REST fetch.
 *   Effect B (accountUrl arrives): If session is actively changing (generating/initializing),
 *     upgrade to Firestore real-time listeners without resetting already-loaded data.
 *     Terminal sessions skip subscription entirely.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Session, Comment, api } from '../services/api';
import { subscribeToSession, subscribeToComments } from '../services/firestoreListeners';
import { encodeAccountId } from '../services/common';
import { isFirebaseInitialized } from '../config/firebase';

// Session statuses that are actively changing and benefit from real-time updates
const ACTIVE_STATUSES = new Set(['generating', 'initializing']);

// Session statuses that are terminal — one REST fetch is sufficient
const TERMINAL_STATUSES = new Set(['completed', 'awaiting_selection', 'error', 'needs_login', 'posting']);

interface UseSessionSubscriptionResult {
    session: Session | null;
    comments: Comment[];
    loading: boolean;
    error: string | null;
    isUsingRealtime: boolean;
    totalCommentCount: number;
    hasStartedReviewing: boolean;
}

interface UseSessionSubscriptionOptions {
    /** Enable polling even when Firestore is working (for debugging) */
    forcePolling?: boolean;
    /** Polling interval in milliseconds (default: 5000) */
    pollingInterval?: number;
    /** Pre-seeded data from cache for instant display (background refresh still happens) */
    initialData?: {
        session: Session;
        comments: Comment[];
    };
}

/**
 * Hook to subscribe to session and comment updates with Firestore real-time listeners
 * Falls back to REST API polling if Firestore fails
 *
 * @param sessionId - Session ID to subscribe to
 * @param accountUrl - Account URL (used to derive accountId for Firestore path)
 * @param options - Configuration options
 */
export function useSessionSubscription(
    sessionId: string | undefined,
    accountUrl: string | undefined,
    options: UseSessionSubscriptionOptions = {}
): UseSessionSubscriptionResult {
    const { forcePolling = false, pollingInterval = 5000, initialData } = options;

    const [session, setSession] = useState<Session | null>(null);
    const [comments, setComments] = useState<Comment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isUsingRealtime, setIsUsingRealtime] = useState(false);
    const [totalCommentCount, setTotalCommentCount] = useState(0);
    const [hasStartedReviewing, setHasStartedReviewing] = useState(false);

    // Track last comment timestamp for incremental loading
    const lastCommentTimestampRef = useRef<string | null>(null);

    // Unsubscribe functions for cleanup
    const sessionUnsubRef = useRef<(() => void) | null>(null);
    const commentsUnsubRef = useRef<(() => void) | null>(null);

    // Polling timer ref
    const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Use refs to hold the latest values needed by callbacks,
    // so the main effect doesn't depend on callback identity changes.
    const hasStartedReviewingRef = useRef(hasStartedReviewing);
    hasStartedReviewingRef.current = hasStartedReviewing;
    const sessionStatusRef = useRef<string | undefined>(session?.status);
    sessionStatusRef.current = session?.status;

    // Track whether Effect B (Firestore) has already been set up for this session
    const firestoreSetupForRef = useRef<string | null>(null);

    // Stop polling helper (stable - no deps)
    const stopPolling = useCallback(() => {
        if (pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
    }, []);

    // Cleanup listeners helper (stable - stopPolling is stable)
    const cleanupListeners = useCallback(() => {
        stopPolling();
        if (sessionUnsubRef.current) {
            sessionUnsubRef.current();
            sessionUnsubRef.current = null;
        }
        if (commentsUnsubRef.current) {
            commentsUnsubRef.current();
            commentsUnsubRef.current = null;
        }
    }, [stopPolling]);

    // REST API fetch - reads hasStartedReviewing from ref to keep stable identity
    const fetchViaRestApi = useCallback(async (sid: string) => {
        try {
            const sessionData = await api.getSession(sid);
            setSession(sessionData);

            // Determine if we should use incremental loading
            if (ACTIVE_STATUSES.has(sessionData.status)) {
                try {
                    const response = await api.getCommentsIncremental(
                        sid,
                        lastCommentTimestampRef.current || undefined,
                        50
                    );

                    setTotalCommentCount(response.totalCount);

                    if (response.comments.length > 0) {
                        // Merge new comments
                        setComments((prev) => {
                            const existingIds = new Set(prev.map((c) => c.id));
                            const newComments = response.comments.filter((c) => !existingIds.has(c.id));
                            return [...prev, ...newComments];
                        });

                        // Update timestamp for next fetch
                        const lastComment = response.comments[response.comments.length - 1];
                        if (lastComment.createdAt) {
                            lastCommentTimestampRef.current = lastComment.createdAt;
                        }

                        if (!hasStartedReviewingRef.current) {
                            setHasStartedReviewing(true);
                        }
                    }
                } catch (err) {
                    console.error('[useSessionSubscription] Incremental fetch failed:', err);
                }
            } else if (TERMINAL_STATUSES.has(sessionData.status)) {
                // Fetch all comments when generation is complete
                const commentsData = await api.getComments(sid);
                setComments(commentsData);
                setTotalCommentCount(commentsData.length);
            }

            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch session');
        } finally {
            setLoading(false);
        }
    }, []); // Stable: no state deps, reads from refs

    // Start polling - reads session status from ref to keep stable identity
    const startPolling = useCallback((sid: string) => {
        if (pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
        }

        // Initial fetch
        fetchViaRestApi(sid);

        // Set up interval — only poll if session is still in an active state
        pollingTimerRef.current = setInterval(() => {
            const status = sessionStatusRef.current;
            if (!status || ACTIVE_STATUSES.has(status)) {
                fetchViaRestApi(sid);
            }
        }, pollingInterval);
    }, [fetchViaRestApi, pollingInterval]); // Stable: fetchViaRestApi and pollingInterval are stable

    // Setup Firestore listeners
    const setupFirestoreListeners = useCallback((sid: string, url: string) => {
        const accountId = encodeAccountId(url);
        let hasFallenBack = false;

        // Helper to unsubscribe both listeners and fall back to polling once
        const fallbackToPolling = (err: Error) => {
            if (hasFallenBack) return; // Prevent multiple fallbacks
            hasFallenBack = true;
            console.error('[useSessionSubscription] Firestore listener error, falling back to polling:', err);

            // Unsubscribe both listeners to stop error callbacks
            if (sessionUnsubRef.current) {
                sessionUnsubRef.current();
                sessionUnsubRef.current = null;
            }
            if (commentsUnsubRef.current) {
                commentsUnsubRef.current();
                commentsUnsubRef.current = null;
            }

            setIsUsingRealtime(false);
            startPolling(sid);
        };

        // Subscribe to session
        sessionUnsubRef.current = subscribeToSession(
            accountId,
            sid,
            (sessionData) => {
                setSession(sessionData);
                setLoading(false);
                setError(null);
                setIsUsingRealtime(true);
            },
            fallbackToPolling
        );

        // Subscribe to comments
        commentsUnsubRef.current = subscribeToComments(
            accountId,
            sid,
            (commentsData) => {
                setComments(commentsData);
                setTotalCommentCount(commentsData.length);
                if (commentsData.length > 0 && !hasStartedReviewingRef.current) {
                    setHasStartedReviewing(true);
                }
            },
            fallbackToPolling
        );
    }, [startPolling]); // Stable: startPolling is stable

    // Gap polling: bridge the window between Effect A's one-shot REST fetch
    // and Effect B's Firestore setup (which waits for accountUrl).
    const startGapPollingIfNeeded = useCallback((sid: string) => {
        // Firestore already active — no gap to fill
        if (firestoreSetupForRef.current !== null) return;
        // Session is terminal — no updates expected
        const status = sessionStatusRef.current;
        if (status && TERMINAL_STATUSES.has(status)) return;
        // Already polling
        if (pollingTimerRef.current) return;

        console.log('[useSessionSubscription] Starting gap polling (waiting for Firestore)');
        pollingTimerRef.current = setInterval(() => {
            // Self-terminate when Firestore takes over or session goes terminal
            if (firestoreSetupForRef.current !== null) {
                console.log('[useSessionSubscription] Firestore now active — stopping gap polling');
                stopPolling();
                return;
            }
            const currentStatus = sessionStatusRef.current;
            if (currentStatus && TERMINAL_STATUSES.has(currentStatus)) {
                console.log('[useSessionSubscription] Session no longer active — stopping gap polling');
                stopPolling();
                return;
            }
            fetchViaRestApi(sid);
        }, pollingInterval);
    }, [fetchViaRestApi, pollingInterval, stopPolling]); // Stable: all deps are stable

    // Use a ref to track initialData so the effect doesn't re-run when
    // the object identity changes but the sessionId stays the same.
    const initialDataRef = useRef(initialData);
    initialDataRef.current = initialData;

    // ===================================================================
    // Effect A: sessionId changes — full reset + one-time REST fetch
    // ===================================================================
    useEffect(() => {
        if (!sessionId) {
            setLoading(false);
            return;
        }

        // Clean up any previous listeners/polling
        cleanupListeners();

        // Reset shared state
        setError(null);
        setIsUsingRealtime(false);
        lastCommentTimestampRef.current = null;
        firestoreSetupForRef.current = null;

        const seed = initialDataRef.current;
        if (seed) {
            // Seed from cached data — instant display, no loading spinner
            setSession(seed.session);
            setComments(seed.comments);
            setTotalCommentCount(seed.comments.length);
            setHasStartedReviewing(seed.comments.length > 0);
            setLoading(false);

            // Background refresh for full data (caption, vncUrl, error, etc.)
            // Don't set loading=true — the user already sees content
            fetchViaRestApi(sessionId).then(() => startGapPollingIfNeeded(sessionId));
        } else {
            // No cache — full reset with loading spinner
            setLoading(true);
            setSession(null);
            setComments([]);
            setTotalCommentCount(0);
            setHasStartedReviewing(false);

            fetchViaRestApi(sessionId).then(() => startGapPollingIfNeeded(sessionId));
        }

        return cleanupListeners;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks are stable (use refs, not state), safe to omit
    }, [sessionId]);

    // ===================================================================
    // Effect B: accountUrl arrives — upgrade to Firestore for active sessions only
    // ===================================================================
    useEffect(() => {
        if (!sessionId || !accountUrl) return;
        if (forcePolling || !isFirebaseInitialized()) return;

        // Don't set up Firestore twice for the same session+accountUrl
        const key = `${sessionId}:${accountUrl}`;
        if (firestoreSetupForRef.current === key) return;

        // Only subscribe to Firestore for sessions that are actively changing
        const status = sessionStatusRef.current;
        if (status && TERMINAL_STATUSES.has(status)) {
            // Terminal session — the REST fetch from Effect A is sufficient.
            // No real-time subscription needed.
            console.log('[useSessionSubscription] Terminal session, skipping Firestore subscription');
            return;
        }

        // Active or unknown status — set up Firestore listeners
        console.log('[useSessionSubscription] Setting up Firestore real-time listeners for active session');
        firestoreSetupForRef.current = key;

        // Clean up any polling that Effect A may have NOT started (it only does a one-shot fetch),
        // but clean up just in case of race conditions
        stopPolling();
        if (sessionUnsubRef.current) {
            sessionUnsubRef.current();
            sessionUnsubRef.current = null;
        }
        if (commentsUnsubRef.current) {
            commentsUnsubRef.current();
            commentsUnsubRef.current = null;
        }

        setupFirestoreListeners(sessionId, accountUrl);

        return () => {
            // Only clean up if this is still the active setup
            if (firestoreSetupForRef.current === key) {
                if (sessionUnsubRef.current) {
                    sessionUnsubRef.current();
                    sessionUnsubRef.current = null;
                }
                if (commentsUnsubRef.current) {
                    commentsUnsubRef.current();
                    commentsUnsubRef.current = null;
                }
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks are stable (use refs, not state), safe to omit
    }, [sessionId, accountUrl, forcePolling]);

    return {
        session,
        comments,
        loading,
        error,
        isUsingRealtime,
        totalCommentCount,
        hasStartedReviewing,
    };
}
